import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MapPin, Search, Eye } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";
import { TheatreImage } from "@/components/TheatreImage";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function TheatreCard({ venue, info }) {
  const body = (
    <>
      <div className="flex items-center gap-2 px-4 py-3">
        <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-[hsl(var(--gold))]" />
        <span className="font-serif text-sm font-semibold leading-tight">{venue.name}</span>
      </div>
      <TheatreImage
        key={info?.image_url}
        src={info?.image_url}
        alt={`${venue.name} exterior`}
        className="aspect-[4/3] w-full"
      />
      {!info?.slug && <p className="px-4 py-2 text-xs text-muted-foreground">No guide yet</p>}
    </>
  );

  if (info?.slug) {
    return (
      <Link
        to={`/theatres/${info.slug}`}
        className="group block overflow-hidden rounded-xl border border-border bg-card transition-all hover:-translate-y-1 hover:border-primary hover:shadow-lg"
      >
        {body}
      </Link>
    );
  }
  return <div className="overflow-hidden rounded-xl border border-border bg-card opacity-70">{body}</div>;
}

function TheatreRow({ venue, info }) {
  const label = (
    <span className="flex items-center gap-2 font-medium">
      <MapPin className="h-3.5 w-3.5 text-[hsl(var(--gold))]" /> {venue.name}
    </span>
  );

  if (info?.slug) {
    return (
      <Link
        to={`/theatres/${info.slug}`}
        className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm transition-colors hover:border-primary/60"
      >
        {label}
      </Link>
    );
  }
  return (
    <div className="flex items-center justify-between rounded-xl border border-dashed border-border bg-card px-4 py-3 text-sm opacity-70">
      {label}
      <span className="text-xs text-muted-foreground">No guide yet</span>
    </div>
  );
}

export default function TheatreGuide() {
  return (
    <>
      <Seo
        title="Theatre A-Z: seating guides for London theatres"
        description="Seating notes, restricted-view warnings and accessibility details for London's theatres, to help you choose the right seat before you book."
      />
      <PageHero
        offset={5}
        overline="A to Z"
        title="Theatre A-Z"
        callout={{
          icon: Eye,
          lead: "Choose your seat with confidence.",
          text: "Every guide has seating notes, restricted-view warnings and accessibility details. Sources sometimes disagree, so check your exact seat on the seller's seat map before you book.",
        }}
      />
      <TheatreGuideContent />
    </>
  );
}

function TheatreGuideContent() {
  const [venues, setVenues] = useState([]);
  const [info, setInfo] = useState([]);
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState(null);

  useEffect(() => {
    api.get("/venues").then((r) => setVenues(r.data)).catch(() => setVenues([]));
    api.get("/theatre-info").then((r) => setInfo(r.data)).catch(() => setInfo([]));
  }, []);

  const activeLetter = q ? null : letter;

  const infoByName = useMemo(() => {
    const map = {};
    info.forEach((i) => { map[i.name] = i; });
    return map;
  }, [info]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return venues
      .filter((v) => !term || v.name.toLowerCase().includes(term))
      .filter((v) => !activeLetter || v.name[0]?.toUpperCase() === activeLetter)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [venues, q, activeLetter]);

  const availableLetters = useMemo(
    () => new Set(venues.map((v) => v.name[0]?.toUpperCase())),
    [venues]
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="relative max-w-lg">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search theatres…"
          className="w-full rounded-full border border-input bg-card py-2.5 pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {!q && (
        <div className="mt-4 flex flex-wrap gap-1">
          <button
            onClick={() => setLetter(null)}
            className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
              letter === null ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
            }`}
          >
            All
          </button>
          {LETTERS.map((l) => (
            <button
              key={l}
              onClick={() => setLetter(l)}
              disabled={!availableLetters.has(l)}
              className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
                letter === l
                  ? "bg-primary text-primary-foreground"
                  : availableLetters.has(l)
                  ? "text-foreground hover:bg-secondary"
                  : "text-muted-foreground/30"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 && <p className="mt-8 text-sm text-muted-foreground">No theatres match.</p>}

      {activeLetter ? (
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((v) => (
            <TheatreCard key={v.name} venue={v} info={infoByName[v.name]} />
          ))}
        </div>
      ) : (
        <div className="mt-8 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((v) => (
            <TheatreRow key={v.name} venue={v} info={infoByName[v.name]} />
          ))}
        </div>
      )}
    </div>
  );
}